import { forwardRef, useImperativeHandle, useState, useEffect } from "react";
import { useEditor, EditorContent, BubbleMenu } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import Mention from "@tiptap/extension-mention";
import Placeholder from "@tiptap/extension-placeholder";
import Typography from "@tiptap/extension-typography";
import TextStyle from "@tiptap/extension-text-style";
import HorizontalRule from "@tiptap/extension-horizontal-rule";
import { cn } from "@/lib/utils";
import { Button } from "./button";
import { Input } from "./input";
import {
  Bold,
  Italic,
  List,
  ListOrdered,
  Link as LinkIcon,
  Image as ImageIcon,
  Undo,
  Redo,
  Code,
  X,
  Check,
} from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { suggestion } from "./mention-suggestion";

interface RichTextEditorProps {
  content?: string;
  onChange?: (content: string, html: string) => void;
  placeholder?: string;
  className?: string;
  mentions?: Array<{ id: string; label: string; email?: string }>;
  onMentionSelect?: (mention: { id: string; label: string }) => void;
}

export interface RichTextEditorRef {
  getContent: () => { text: string; html: string };
  setContent: (html: string) => void;
  focus: () => void;
  focusEnd: () => void;
  clear: () => void;
}

import "./rich-text-editor.css";

export const RichTextEditor = forwardRef<
  RichTextEditorRef,
  RichTextEditorProps
>(
  (
    {
      content = "",
      onChange,
      placeholder,
      className,
      mentions = [],
      onMentionSelect,
    },
    ref
  ) => {
    const [linkUrl, setLinkUrl] = useState("");
    const [imageUrl, setImageUrl] = useState("");
    const [isLinkOpen, setIsLinkOpen] = useState(false);
    const [isImageOpen, setIsImageOpen] = useState(false);

    const editor = useEditor({
      extensions: [
        StarterKit.configure({
          heading: {
            levels: [1, 2, 3],
          },
          bulletList: {
            HTMLAttributes: {
              class: "list-disc",
            },
          },
          orderedList: {
            HTMLAttributes: {
              class: "list-decimal",
            },
          },
          blockquote: {
            HTMLAttributes: {
              class: "border-l-4 border-gray-300 pl-4 italic",
            },
          },
          codeBlock: {
            HTMLAttributes: {
              class:
                "bg-gray-100 dark:bg-gray-800 p-4 rounded-md font-mono text-sm",
            },
          },
        }),
        Typography,
        TextStyle,
        Placeholder.configure({
          placeholder: placeholder || "Write something...",
        }),
        Link.configure({
          openOnClick: false,
          HTMLAttributes: {
            class: "text-blue-600 hover:text-blue-800 underline cursor-pointer",
          },
        }),
        Image.configure({
          HTMLAttributes: {
            class: "max-w-full h-auto rounded-md my-2",
          },
        }),
        Mention.configure({
          HTMLAttributes: {
            class:
              "mention bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-100 px-1 py-0.5 rounded text-sm font-medium",
          },
          suggestion: suggestion(mentions, onMentionSelect),
        }),
        HorizontalRule,
      ],

      content,
      onUpdate: ({ editor }) => {
        const html = editor.getHTML();
        const text = editor.getText();
        onChange?.(text, html);
      },
      editorProps: {
        attributes: {
          class:
            "prose prose-sm sm:prose lg:prose-lg xl:prose-2xl focus:outline-none max-w-none [&_ul]:list-disc [&_ol]:list-decimal [&_ul]:ml-6 [&_ol]:ml-6",
        },
      },
    });    // Sync content prop with editor
    useEffect(() => {
      if (editor && content !== undefined && content !== editor.getHTML()) {
        editor.commands.setContent(content);
      }
    }, [editor, content]);

    useImperativeHandle(ref, () => ({
      getContent: () => ({
        text: editor?.getText() || "",
        html: editor?.getHTML() || "",
      }),
      setContent: (html: string) => {
        editor?.commands.setContent(html);
      },
      focus: () => {
        editor?.commands.focus();
      },
      focusEnd: () => {
        editor?.commands.focus('end');
      },
      clear: () => {
        editor?.commands.clearContent();
      },
    }));

    if (!editor) {
      return null;
    }

    const addLink = () => {
      if (linkUrl) {
        editor.chain().focus().setLink({ href: linkUrl }).run();
        setLinkUrl("");
        setIsLinkOpen(false);
      }
    };

    const removeLink = () => {
      editor.chain().focus().unsetLink().run();
      setIsLinkOpen(false);
    };

    const addImage = () => {
      if (imageUrl) {
        editor.chain().focus().setImage({ src: imageUrl }).run();
        setImageUrl("");
        setIsImageOpen(false);
      }
    };

    const isLinkActive = editor.isActive("link");

    return (
      <div className={cn("border border-input rounded-md", className)}>
        {/* Bubble Menu for selection */}
        {editor && (
          <BubbleMenu
            editor={editor}
            tippyOptions={{ duration: 100 }}
            className="bg-background border border-border rounded-md shadow-md flex items-center gap-1 p-1"
          >
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => editor.chain().focus().toggleBold().run()}
              className={cn(
                "h-7 w-7 p-0",
                editor.isActive("bold") && "bg-accent"
              )}
            >
              <Bold className="h-3 w-3" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => editor.chain().focus().toggleItalic().run()}
              className={cn(
                "h-7 w-7 p-0",
                editor.isActive("italic") && "bg-accent"
              )}
            >
              <Italic className="h-3 w-3" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() =>
                editor
                  .chain()
                  .focus()
                  .toggleNode("codeBlock", "paragraph")
                  .run()
              }
              className={cn(
                "h-7 w-7 p-0",
                editor.isActive("codeBlock") && "bg-accent"
              )}
            >
              <Code className="h-3 w-3" />
            </Button>
          </BubbleMenu>
        )}

        {/* Main Toolbar */}
        <div className="flex flex-wrap items-center gap-1 p-2 border-b border-input bg-muted/50">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => editor.chain().focus().toggleBold().run()}
            className={cn(
              "h-7 w-7 p-0",
              editor.isActive("bold") && "bg-accent"
            )}
            title="Bold"
          >
            <Bold className="h-3 w-3" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => editor.chain().focus().toggleItalic().run()}
            className={cn(
              "h-7 w-7 p-0",
              editor.isActive("italic") && "bg-accent"
            )}
            title="Italic"
          >
            <Italic className="h-3 w-3" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => editor.chain().focus().toggleMark("code").run()}
            className={cn(
              "h-7 w-7 p-0",
              editor.isActive("code") && "bg-accent"
            )}
            title="Code"
          >
            <Code className="h-3 w-3" />
          </Button>

          <div className="mx-1 h-4 w-px bg-border" />

          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            className={cn(
              "h-7 w-7 p-0",
              editor.isActive("bulletList") && "bg-accent"
            )}
            title="Bullet List"
          >
            <List className="h-3 w-3" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            className={cn(
              "h-7 w-7 p-0",
              editor.isActive("orderedList") && "bg-accent"
            )}
            title="Numbered List"
          >
            <ListOrdered className="h-3 w-3" />
          </Button>

          <div className="mx-1 h-4 w-px bg-border" />

          {/* Link Popover */}
          <Popover open={isLinkOpen} onOpenChange={setIsLinkOpen}>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className={cn("h-7 w-7 p-0", isLinkActive && "bg-accent")}
                title="Add Link"
              >
                <LinkIcon className="h-3 w-3" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80 p-3">
              <div className="space-y-2">
                <div className="text-sm font-medium">
                  {isLinkActive ? "Edit Link" : "Add Link"}
                </div>
                <div className="flex gap-2">
                  <Input
                    placeholder="Enter URL..."
                    value={linkUrl}
                    onChange={(e) => setLinkUrl(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && addLink()}
                    className="h-8"
                  />
                  <Button
                    type="button"
                    size="sm"
                    onClick={addLink}
                    disabled={!linkUrl}
                    className="h-8 w-8 p-0"
                  >
                    <Check className="h-3 w-3" />
                  </Button>
                  {isLinkActive && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={removeLink}
                      className="h-8 w-8 p-0"
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  )}
                </div>
              </div>
            </PopoverContent>
          </Popover>

          {/* Image Popover */}
          <Popover open={isImageOpen} onOpenChange={setIsImageOpen}>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0"
                title="Add Image"
              >
                <ImageIcon className="h-3 w-3" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80 p-3">
              <div className="space-y-2">
                <div className="text-sm font-medium">Add Image</div>
                <div className="flex gap-2">
                  <Input
                    placeholder="Enter image URL..."
                    value={imageUrl}
                    onChange={(e) => setImageUrl(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && addImage()}
                    className="h-8"
                  />
                  <Button
                    type="button"
                    size="sm"
                    onClick={addImage}
                    disabled={!imageUrl}
                    className="h-8 w-8 p-0"
                  >
                    <Check className="h-3 w-3" />
                  </Button>
                </div>
                <div className="text-xs text-muted-foreground">
                  Tip: Upload images to a service like Imgur or use direct URLs
                </div>
              </div>
            </PopoverContent>
          </Popover>

          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => editor.chain().focus().undo().run()}
            disabled={!editor.can().undo()}
            className="h-7 w-7 p-0"
            title="Undo"
          >
            <Undo className="h-3 w-3" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => editor.chain().focus().redo().run()}
            disabled={!editor.can().redo()}
            className="h-7 w-7 p-0"
            title="Redo"
          >
            <Redo className="h-3 w-3" />
          </Button>
        </div>

        <EditorContent
          editor={editor}
          className={cn(
            "p-3 min-h-[100px] focus-within:outline-none",
            "rich-text-editor-content"
          )}
        />
      </div>
    );
  }
);

RichTextEditor.displayName = "RichTextEditor";
